<template>
    <dialog ref="modalRef" class="modal">
        <div class="modal-box">
            <h3 class="font-bold text-lg mb-4">Adicionar cartão</h3>
            <p class="text-sm text-gray-500 mb-4">Seu cartão será salvo com segurança via Stripe.</p>

            <div class="form-control mb-3">
                <label class="label py-1">
                    <span class="label-text text-sm">Nome impresso no cartão</span>
                </label>
                <input v-model="cardholderName" type="text" placeholder="Como aparece no cartão" class="input input-bordered w-full" />
            </div>

            <div class="form-control mb-3">
                <label class="label py-1">
                    <span class="label-text text-sm">Dados do cartão</span>
                </label>
                <div id="sirim-card-element" class="border rounded-lg p-3 bg-white min-h-[44px]"></div>
            </div>

            <p v-if="errorMessage" class="text-red-600 text-sm mt-2">{{ errorMessage }}</p>

            <div class="modal-action">
                <button class="btn btn-ghost" @click="closeModal" :disabled="loading">Cancelar</button>
                <button class="btn btn-primary text-white" @click="submitCard" :disabled="loading || !stripeReady">
                    <span v-if="loading" class="loading loading-spinner loading-sm"></span>
                    Salvar cartão
                </button>
            </div>
        </div>
        <form method="dialog" class="modal-backdrop">
            <button>fechar</button>
        </form>
    </dialog>
</template>

<script>
import { ref, onBeforeUnmount, defineExpose } from 'vue';
import { loadStripe } from '@stripe/stripe-js';
import { SubscriptionService } from '@/services/Entity';

const STRIPE_ERROR_MESSAGES = {
    expired_card: 'Cartão vencido.',
    invalid_number: 'Número do cartão inválido.',
    incorrect_number: 'Número do cartão incorreto.',
    invalid_cvc: 'CVV inválido.',
    incorrect_cvc: 'CVV incorreto.',
    card_declined: 'Cartão recusado. Tente outro cartão.',
    insufficient_funds: 'Saldo insuficiente.',
    processing_error: 'Erro ao processar. Tente novamente.',
};

export default {
    emits: ['card-added'],
    setup(props, { emit }) {
        const modalRef = ref(null);
        const cardholderName = ref('');
        const errorMessage = ref('');
        const loading = ref(false);
        const stripeReady = ref(false);
        let stripeInstance = null;
        let cardElement = null;

        async function initStripe() {
            if (stripeInstance) return;
            const publicKey = process.env.VUE_APP_STRIPE_PUBLIC_KEY;
            if (!publicKey) {
                errorMessage.value = 'Stripe não configurado (VITE_STRIPE_PUBLIC_KEY).';
                return;
            }
            stripeInstance = await loadStripe(publicKey);
            if (!stripeInstance) {
                errorMessage.value = 'Falha ao carregar Stripe. Recarregue a página.';
                return;
            }
            const elements = stripeInstance.elements({
                appearance: {
                    theme: 'stripe',
                    variables: { colorPrimary: '#0891b2', borderRadius: '8px', fontSizeBase: '15px' }
                }
            });
            cardElement = elements.create('card', {
                hidePostalCode: true,
                disableLink: true,
                style: {
                    base: { fontSize: '16px', color: '#374151', '::placeholder': { color: '#9ca3af' } },
                    invalid: { color: '#dc2626' }
                }
            });
            cardElement.mount('#sirim-card-element');
            stripeReady.value = true;
        }

        async function openModal() {
            errorMessage.value = '';
            cardholderName.value = '';
            if (modalRef.value && typeof modalRef.value.showModal === 'function') {
                modalRef.value.showModal();
            }
            await initStripe();
        }

        function closeModal() {
            if (modalRef.value && typeof modalRef.value.close === 'function') {
                modalRef.value.close();
            }
        }

        function translateError(error) {
            if (!error) return 'Não foi possível salvar o cartão.';
            return STRIPE_ERROR_MESSAGES[error.code] || STRIPE_ERROR_MESSAGES[error.decline_code] || 'Não foi possível salvar o cartão.';
        }

        async function submitCard() {
            errorMessage.value = '';
            if (!cardholderName.value.trim()) {
                errorMessage.value = 'Informe o nome impresso no cartão.';
                return;
            }
            if (!stripeInstance || !cardElement) {
                errorMessage.value = 'Stripe não inicializou corretamente.';
                return;
            }
            loading.value = true;
            try {
                const { data } = await SubscriptionService.createSetupIntent();
                const clientSecret = data && data.client_secret;
                if (!clientSecret) throw new Error('Falha ao iniciar setup do cartão.');

                const result = await stripeInstance.confirmCardSetup(clientSecret, {
                    payment_method: {
                        card: cardElement,
                        billing_details: { name: cardholderName.value.trim() }
                    }
                });

                if (result.error) {
                    errorMessage.value = translateError(result.error);
                    return;
                }

                if (result.setupIntent && result.setupIntent.status === 'succeeded') {
                    emit('card-added', { paymentMethodId: result.setupIntent.payment_method });
                    closeModal();
                    try { cardElement.clear(); } catch (e) { /* ignore */ }
                } else {
                    errorMessage.value = 'Cartão não confirmado. Tente novamente.';
                }
            } catch (err) {
                errorMessage.value = (err && err.message) || 'Erro ao adicionar cartão.';
            } finally {
                loading.value = false;
            }
        }

        onBeforeUnmount(() => {
            if (cardElement) { try { cardElement.unmount(); } catch (e) { /* ignore */ } cardElement = null; }
        });

        defineExpose({ openModal, closeModal });

        return { modalRef, cardholderName, errorMessage, loading, stripeReady, openModal, closeModal, submitCard };
    }
};
</script>
